import React, { useState } from "react";
import { Box, Menu, MenuItem, IconButton } from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import { saveFunctionRegistry } from "../../../state/configurationSlice";
import { StyledHeaderButton } from "../../../layout/Header.styles";
import { useActions } from "../../../hooks/useActions";
import EncompassService from "../../../services/encompassService";
import { useLocation } from "react-router-dom";
import { hasPushbackConfigChanges } from "../../../utils/pushbackConfigChangeDetector";
import { hasPluginConfigChanges } from "../../../utils/pluginConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../../utils/bundlingConfigChangeDetector";
import { getConfigPathFlags } from "../../../utils/pathMatcher";
import { setLoadingState } from "../../../state/appSlice";
import { hasXPathConfigChanges } from "../../../utils/xpathConfigChangeDetector";
import { ActionType } from "../../../state/actions";
import { useHasUnsavedChanges } from "../../../hooks/useHasUnsavedChanges";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import { setGlobalSaveHadChanges } from "../../../utils/globalSaveStatus";


interface ConfigActionButtonsProps {
    onError?: (message: string) => void;
}

const ConfigActionButtons: React.FC<ConfigActionButtonsProps> = ({ onError }) => {
    // Can set from state here, or use direct state values.
    // TODO: Implement Private Save and Private Delete    
    const [privateSaveDisabled] = useState(true);
    const [privateDeleteDisabled] = useState(true);
    
    const dispatch = useDispatch();
    const location = useLocation();
    const { isXPathMappingsPath, isConfigPushbackPath, isConfigBundlingPath } =
        getConfigPathFlags(location.pathname);
    const currentSaveFunctionId = useSelector((state: RootState) => state.configuration.currentSaveFunctionId);
    const currentSaveFunction = currentSaveFunctionId ? saveFunctionRegistry.getCurrentFunction() : null;
    const { saveConfigXPathData, saveConfigPushbackData, saveConfigBundleData, saveConfigBundleEventData, saveConfigPlugInData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const configId = useSelector((state: RootState) => state.appSlice.configId);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pluginConfiguration = useSelector((state: RootState) => state.configPlugInData.data);
    const persistedPluginConfiguration = useSelector((state: RootState) => state.configPlugInData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);
    
    // Define all available actions
    const actions = [
        {
            label: 'Save',
            handler: () => {
                setSaveDialogOpen(true);
            },
            disabled: false
        },
        {
            label: 'Private Save',
            handler: () => { if (!privateSaveDisabled) return; },
            disabled: privateSaveDisabled
        },
        {
            label: 'Private Delete',
            handler: () => { if (!privateDeleteDisabled) return; },
            disabled: privateDeleteDisabled
        }
    ];
    
    // Track selected action
    const [selectedAction, setSelectedAction] = useState(actions[0]);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const hasUnsavedChanges = useHasUnsavedChanges();
    
    const executeSave = async () => {
        dispatch(setLoadingState({ loading: true, loadingMessage: "Saving changes..." }));
        let hasSaveError = false;
        let didSaveAny = false;
        setGlobalSaveHadChanges(false);
        const recordSaveError = (message: string, error: unknown) => {
            hasSaveError = true;
            console.warn(message, error);
            onError?.(message);
        };

        try {
            const resolvedConfigId = configId || (EncompassService.getConfigId() as string);
            if (resolvedConfigId) {
                const hasPluginChanges = pluginConfiguration?.Configuration && persistedPluginConfiguration?.Configuration
                    ? hasPluginConfigChanges(pluginConfiguration.Configuration, persistedPluginConfiguration.Configuration)
                    : false;
                if (hasPluginChanges && pluginConfiguration) {
                    try {
                        await saveConfigPlugInData(resolvedConfigId, pluginConfiguration, accessToken);
                        didSaveAny = true;
                    } catch (error) {
                        recordSaveError('Failed to save plugin configuration', error);
                    }
                }

                if (!isXPathMappingsPath) {
                    const fieldMappings = xPathData?.Configuration?.FieldMappings;
                    const hasXPathChanges = xPathData
                        ? hasXPathConfigChanges(xPathData, persistedXPathData)
                        : false;
                    if (fieldMappings && hasXPathChanges) {
                        try {
                            await saveConfigXPathData(resolvedConfigId, fieldMappings, accessToken);
                            didSaveAny = true;
                        } catch (error) {
                            recordSaveError('Failed to save XPath mappings', error);
                        }
                    }
                }

                if (!isConfigPushbackPath) {
                    const pushbackConfig = pushbackData?.Configuration;
                    const hasPushbackChanges = pushbackData && persistedPushbackData
                        ? hasPushbackConfigChanges(pushbackData, persistedPushbackData)
                        : false;
                    if (pushbackConfig && hasPushbackChanges) {
                        try {
                            await saveConfigPushbackData(resolvedConfigId, pushbackConfig, accessToken);
                            didSaveAny = true;
                        } catch (error) {
                            recordSaveError('Failed to save pushback configuration', error);
                        }
                    }
                }

                if (!isConfigBundlingPath) {
                    const docBundlingConfig = documentBundles?.Configuration?.Bundling;
                    const eventBundlingConfig = eventBundles?.Configuration?.Bundling;
                    const hasDocumentBundlingChange = documentBundles && persistedDocumentBundles
                        ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles)
                        : false;
                    const hasEventBundlingChange = eventBundles && persistedEventBundles
                        ? hasEventBundlingChanges(eventBundles, persistedEventBundles)
                        : false;

                    if (docBundlingConfig && hasDocumentBundlingChange) {
                        try {
                            await saveConfigBundleData(resolvedConfigId, docBundlingConfig, accessToken);
                            didSaveAny = true;
                        } catch (error) {
                            recordSaveError('Failed to save document bundling', error);
                        }
                    }

                    if (eventBundlingConfig && hasEventBundlingChange) {
                        try {
                            await saveConfigBundleEventData(resolvedConfigId, eventBundlingConfig, accessToken);
                            didSaveAny = true;
                        } catch (error) {
                            recordSaveError('Failed to save event bundling', error);
                        }
                    }
                }
            }

            const shouldFlagGlobalSave = didSaveAny && !hasSaveError;
            try {
                if (currentSaveFunction) {
                    setGlobalSaveHadChanges(shouldFlagGlobalSave);
                    await Promise.resolve(currentSaveFunction());
                } else {
                    setGlobalSaveHadChanges(false);
                    console.warn('No save function available for this configuration page');
                }
            } catch (error) {
                setGlobalSaveHadChanges(false);
                recordSaveError('Failed to save changes', error);
            }
        } finally {
            dispatch(setLoadingState({ loading: false }));
        }
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handleActionSelect = (action: typeof actions[0]) => {
        setSelectedAction(action);
        handleDropdownClose();
        action.handler();
    };

    const handleDiscard = () => {
        setSaveDialogOpen(false);
        dispatch({ type: ActionType.REVERT_CONFIG_PLUGIN_DATA });
        if (persistedXPathData) {
            dispatch({
                type: ActionType.GET_CONFIG_XPATH_DATA_SUCCESS,
                payload: persistedXPathData,
                fromServer: false
            });
        }
        if (persistedPushbackData) {
            dispatch({
                type: ActionType.GET_CONFIG_PUSHBACK_SUCCESS,
                payload: persistedPushbackData,
                fromServer: false
            });
        }
        if (persistedDocumentBundles) {
            dispatch({
                type: ActionType.GET_CONFIG_BUNDLE_DATA_SUCCESS,
                payload: persistedDocumentBundles,
                fromServer: false
            });
        }
        if (persistedEventBundles) {
            dispatch({
                type: ActionType.GET_CONFIG_BUNDLE_EVENT_DATA_SUCCESS,
                payload: persistedEventBundles,
                fromServer: false
            });
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: 'center'
                }}
            >
                <StyledHeaderButton
                    onClick={selectedAction.handler}
                    size='small'
                    startIcon={<SaveIcon />}
                    disabled={selectedAction.disabled}
                >
                    {selectedAction.label}
                </StyledHeaderButton>
                <IconButton
                    size="small"
                    onClick={handleDropdownClick}
                    sx={{ ml: 0.5, color: "inherit" }}
                >
                    <ArrowDropDownIcon />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleDropdownClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {actions
                        .filter(action => action.label !== selectedAction.label)
                        .map(action => (
                            <MenuItem 
                                key={action.label}
                                onClick={() => handleActionSelect(action)}
                                disabled={action.disabled}
                            >
                                {action.label}
                            </MenuItem>
                        ))
                    }
                </Menu>
            </Box>
            <UnsavedChangesDialog
                open={saveDialogOpen}
                onCancel={() => setSaveDialogOpen(false)}
                onDiscard={handleDiscard}
                onSaveAndContinue={async () => {
                    setSaveDialogOpen(false);
                    await executeSave();
                }}
                loading={false}
                title="Save Configuration"
                message={hasUnsavedChanges
                    ? "You have unsaved changes. This will overwrite the existing configuration. Do you want to continue?"
                    : "This will overwrite the existing configuration. Do you want to continue?"}
                saveLabel="Save"
                showDiscard={false}
                infoMessage="Please refresh the screen after save."
            />
        </>
    );
};

export default ConfigActionButtons;
